import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import OTPVerifier from "@/components/OTPVerifier";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";

export default function ContactPage() {
    const [searchParams] = useSearchParams();
    const isAmbassador = searchParams.get("interest") === "ambassador";
    const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", message: "" });
    const [token, setToken] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (isAmbassador) {
            setForm((f) => ({
                ...f,
                message: "I'd like to become a Founding Country Ambassador for ZSGI. My country: ____. A little about me and why I want to represent ZSGI: ",
            }));
        }
    }, [isAmbassador]);

    const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!token) {
            toast.error("Please verify your email first");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contact", { ...form, verification_token: token });
            setSubmitted(true);
            toast.success("Message received. We'll be in touch.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section data-testid="contact-page" className="min-h-screen pt-32 md:pt-40 pb-24 md:pb-40">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="label-mono">— {isAmbassador ? "AMBASSADOR APPLICATION" : "CONTACT"}</div>
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
                            className="serif-display text-5xl md:text-7xl lg:text-8xl mt-4 leading-[0.95]"
                        >
                            {isAmbassador ? (
                                <>Represent ZSGI in <span className="serif-italic text-[var(--gold)]">your country.</span></>
                            ) : (
                                <>Let's build <span className="serif-italic text-[var(--gold)]">something</span> that lasts.</>
                            )}
                        </motion.h1>
                        <p className="mt-8 text-lg text-[var(--ivory)]/80 max-w-md">
                            {isAmbassador
                                ? "Tell us who you are and the country you'd represent. Verify your email and our team will reach out about the Founding Country Ambassador programme."
                                : "For partnerships, media, cohort enquiries or speaking invitations — write to us. A human will read every message."}
                        </p>
                        <div className="mt-10 space-y-2 label-mono text-[var(--ivory)]/70">
                            <div data-testid="contact-email">connect@zsgi.org</div>
                            <div data-testid="contact-address">
                                Z ED School of Global Impact Limited<br />
                                128 City Road, London, EC1V 2NX, United Kingdom<br />
                                Company No. 17457081
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-6 md:col-start-7">
                        {submitted ? (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="border border-[var(--gold)] p-10"
                                data-testid="contact-success"
                            >
                                <div className="label-mono text-[var(--gold)]">— RECEIVED</div>
                                <h2 className="serif-display text-3xl md:text-4xl mt-4">
                                    Thank you, {form.name.split(" ")[0]}.
                                </h2>
                                <p className="mt-4 text-[var(--ivory)]/80">We've received your message and will reply from connect@zsgi.org shortly.</p>
                                <Link to="/" className="btn-ghost mt-8">Return home</Link>
                            </motion.div>
                        ) : (
                            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                                <div className="grid md:grid-cols-2 gap-8">
                                    <div>
                                        <label className="label-mono block mb-2">Name</label>
                                        <input required className="input-editorial" value={form.name} onChange={change("name")} data-testid="contact-name" />
                                    </div>
                                    <div>
                                        <label className="label-mono block mb-2">Phone</label>
                                        <input className="input-editorial" value={form.phone} onChange={change("phone")} data-testid="contact-phone" placeholder="+91…" />
                                    </div>
                                </div>
                                <div>
                                    <label className="label-mono block mb-2">Email</label>
                                    <input required type="email" className="input-editorial" value={form.email} onChange={change("email")} data-testid="contact-email" />
                                </div>
                                <div>
                                    <label className="label-mono block mb-2">Organization (optional)</label>
                                    <input className="input-editorial" value={form.organization} onChange={change("organization")} data-testid="contact-organization" />
                                </div>
                                <div>
                                    <label className="label-mono block mb-2">Message</label>
                                    <textarea required rows={5} className="input-editorial resize-none" value={form.message} onChange={change("message")} data-testid="contact-message" />
                                </div>

                                <div className="border-t border-[rgba(255,255,240,0.15)] pt-8">
                                    <OTPVerifier
                                        email={form.email}
                                        purpose="contact"
                                        verified={!!token}
                                        verificationToken={token}
                                        onVerified={setToken}
                                    />
                                </div>

                                <button type="submit" disabled={submitting || !token} className="btn-gold" data-testid="contact-submit">
                                    {submitting ? "Sending…" : "Send message"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
